import { colors } from '@/assets/styles/colors';
import React from 'react';


interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  size?: { width: number; height: number };
  backgroundColor?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Skeleton - Primitive skeleton loading placeholder
 * 
 * @description
 * Base skeleton component with pulse animation.
 * Used to create skeleton loaders for complex components like tables, cards, forms, etc.
 * 
 * @example
 * // Fixed width skeleton
 * <Skeleton width="200px" height="20px" />
 * 
 * @example
 * // Percentage width skeleton
 * <Skeleton width="60%" height="16px" />
 * 
 * @example
 * // Custom color skeleton
 * <Skeleton width="100%" height="40px" backgroundColor="#f0f0f0" />
 * 
 * @example
 * // Using size object
 * <Skeleton size={{ width: 20, height: 20 }} />
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '20px',
  size,
  backgroundColor = colors.grays.neutralE5,
  className = '',
  'data-testid': dataTestId,
}) => {
  const finalWidth = size?.width ?? width;
  const finalHeight = size?.height ?? height;
  
  const style = {
    width: typeof finalWidth === 'number' ? `${finalWidth}px` : finalWidth,
    height: typeof finalHeight === 'number' ? `${finalHeight}px` : finalHeight,
    backgroundColor,
  };

  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={style}
      data-testid={dataTestId}
      aria-label="Loading"
      role="status"
    />
  );
};

export default Skeleton;
